import {
  BEGIN_POST_LOGIN_EXPERIMENT_SYNC,
  END_POST_LOGIN_EXPERIMENT_SYNC,
  SET_CHERTO_EXPERIMENTS,
  SET_OPTIMIZELY_EXPERIMENTS,
  SET_OPTIMIZELY_FEATURE_FLAGS,
  SET_RESOLVED_TRACKING_ID,
} from './actionConstants'
import type { AppAction } from './types/ActionTypes'
import { rebucketExperiments as rebucketExperimentsApi } from 'api/experiments'
import { trackClientEvent } from 'analytics/analytics'
import { clearExperimentPanelOverrides } from 'lib/experiments/experimentPanelPersistence'
import { reportLog } from 'services/userMonitoring'
import {
  refreshLabFeatureFlagsForCurrentUser,
  refreshLabRemoteConfigForCurrentUser,
} from './SystemActions'

export function beginPostLoginExperimentSync() {
  return {
    type: BEGIN_POST_LOGIN_EXPERIMENT_SYNC,
  } as const
}

export function endPostLoginExperimentSync() {
  return {
    type: END_POST_LOGIN_EXPERIMENT_SYNC,
  } as const
}

export function trackPostLoginExperimentSyncClientEvent(action: string) {
  trackClientEvent({
    subject: 'post_login_sync',
    action,
    category: 'experiments',
    type: 'nonInteraction',
  })
}

interface ChangedExperimentsOptions {
  includeRemoved?: boolean
}

function getChangedExperimentsWithResetTrigger<TExperiment extends App.ExperimentDetails>(
  nextExperiments: Record<string, TExperiment | undefined>,
  previousExperiments: Record<string, TExperiment | undefined>,
  options: ChangedExperimentsOptions = {},
) {
  const experimentKeys = options.includeRemoved ?
      Array.from(new Set([...Object.keys(nextExperiments), ...Object.keys(previousExperiments)])) :
      Object.keys(nextExperiments)
  const changedExperimentKeys = experimentKeys.filter(
    key => nextExperiments[key]?.variationKey !== previousExperiments[key]?.variationKey,
  )

  const experimentsWithResetTrigger = changedExperimentKeys.reduce<Record<string, TExperiment | undefined>>(
    (experiments, key) => {
      const experiment = nextExperiments[key]
      experiments[key] = experiment ? { ...experiment, triggered: false } : undefined
      return experiments
    },
    {},
  )

  return {
    changedExperimentKeys,
    experimentsWithResetTrigger,
  }
}

/**
 * Re-bucket experiments after user login.
 *
 * Fails silently if the API call fails so the user continues with existing bucketing.
 */
export function rebucketExperiments(): AppAction {
  return async(dispatch, getState) => {
    try {
      trackPostLoginExperimentSyncClientEvent('rebucket_request_started')
      const { currentRegionCode } = getState().geo
      const response = await rebucketExperimentsApi({ regionCode: currentRegionCode })
      trackPostLoginExperimentSyncClientEvent('rebucket_response_received')

      if (response) {
        const { optimizelyExperiments, optimizelyFeatureFlags, resolvedTrackingId } = response
        const hasChertoExperiments = 'chertoExperiments' in response
        const chertoExperiments = response.chertoExperiments ?? {}

        const freshState = getState()
        const previousExperiments = freshState.optimizely.optimizelyExperiments
        const previousChertoExperiments = freshState.cherto.chertoExperiments

        const optimizelyChanges = getChangedExperimentsWithResetTrigger(optimizelyExperiments, previousExperiments)
        const chertoChanges = hasChertoExperiments ?
            getChangedExperimentsWithResetTrigger(
              chertoExperiments,
              previousChertoExperiments,
              { includeRemoved: true },
            ) :
            { changedExperimentKeys: [], experimentsWithResetTrigger: {} }
        trackPostLoginExperimentSyncClientEvent('rebucket_decision_diff_completed')

        dispatch({
          type: SET_OPTIMIZELY_EXPERIMENTS,
          experiments: optimizelyChanges.experimentsWithResetTrigger,
        })

        if (hasChertoExperiments) {
          dispatch({
            type: SET_CHERTO_EXPERIMENTS,
            experiments: chertoChanges.experimentsWithResetTrigger,
          })
        }

        dispatch({
          type: SET_OPTIMIZELY_FEATURE_FLAGS,
          flags: optimizelyFeatureFlags,
        })

        dispatch({
          type: SET_RESOLVED_TRACKING_ID,
          resolvedTrackingId,
        })
        trackPostLoginExperimentSyncClientEvent('rebucket_state_updated')
      } else {
        trackPostLoginExperimentSyncClientEvent('rebucket_response_empty')
      }
    } catch(error) {
      console.error('[Experiments] Failed to re-bucket experiments:', error)
      trackPostLoginExperimentSyncClientEvent('rebucket_failed')

      reportLog('Experiment re-bucketing failed', {
        error: error instanceof Error ? error.message : 'Unknown error',
        errorStack: error instanceof Error ? error.stack : undefined,
      })
    }
  }
}

export function resetExperimentPanelOverrides(): AppAction {
  return (dispatch) => {
    clearExperimentPanelOverrides()
    dispatch(rebucketExperiments())
    dispatch(refreshLabFeatureFlagsForCurrentUser())
    dispatch(refreshLabRemoteConfigForCurrentUser())
  }
}
